import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { AdministrationTableHeaderComponent } from '@modules/administration/components/administration-table-header/administration-table-header.component';
import { UsersAdministrationTableComponent } from '@modules/administration/components/users-administration-table/users-administration-table.component';
import { AdministrationUser } from '@modules/administration/models/administration-user';
import { UserAdminState } from '@modules/administration/models/user-admin-state.enum';
import { ClientAdminService } from '@modules/client/client-admin/client-admin.service';
import { DepartmentDisplay } from '@modules/department/models/department-display';
import { FilterBarService } from '@modules/filters/filter-bar.service';
import { FilterBarComponent } from '@modules/filters/filter-bar/filter-bar.component';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { writeAccessLicenseStates } from '@pixacare/domain';
import { match } from '@pixacare/shared';
import { ActionsByUserState } from '@shared/models/actions-by-user-state';
import { Status } from '@shared/models/enums/status.enum';
import { ActionButton } from '@shared/models/menu-actions/action-buttons';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { CountByKeyPipe } from '@shared/pipes/count-by-key.pipe';
import { ErrorPipe } from '@shared/pipes/error.pipe';
import { RequestResult } from '@shared/request-state/models/request-result';
import { TuiFilterPipe } from '@taiga-ui/cdk';
import { TuiBlockStatus, TuiCardLarge } from '@taiga-ui/layout';
import { Observable } from 'rxjs';
import { DepartmentAdminService } from '../department-admin/department-admin.service';
import { DepartmentUsersAdminService } from './department-users-admin.service';
import { TuiAnimated } from '@taiga-ui/cdk';

@Component({
  hostDirectives: [TuiAnimated],
  selector: 'pxc-department-users-admin',
  imports: [
    AdministrationTableHeaderComponent,
    CommonModule,
    CountByKeyPipe,
    FilterBarComponent,
    LoaderComponent,
    TranslocoDirective,
    ErrorPipe,
    TuiFilterPipe,
    TuiCardLarge,
    TuiBlockStatus,
    UsersAdministrationTableComponent,
  ],
  providers: [DepartmentUsersAdminService, ClientAdminService, DepartmentAdminService, FilterBarService],
  templateUrl: './department-users-admin.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentUsersAdminComponent implements OnInit {
  private readonly departmentUsersAdminService = inject(DepartmentUsersAdminService);
  private readonly departmentAdminService = inject(DepartmentAdminService);
  private readonly translocoService = inject(TranslocoService);

  readonly filterBarService = inject(FilterBarService);

  readonly state$: Observable<RequestResult<AdministrationUser[]>> = this.departmentUsersAdminService.departmentUsers$;
  readonly department$: Observable<DepartmentDisplay> = this.departmentAdminService.department$;

  readonly deleteUserAction: MenuAction = {
    id: MenuActionId.DEPARTMENT_ADMIN_USER_REMOVE,
    hint: this.translocoService.translate("Supprimer l'utilisateur du groupe"),
    icon: '@tui.user-x',
    status: Status.ERROR,
    appearance: 'flat-danger',
    execute: (user: AdministrationUser): void => this.deleteInvitedUser(user),
  };

  readonly departmentUsersActions: ActionsByUserState = {
    [UserAdminState.ENABLED]: [
      {
        id: MenuActionId.DEPARTMENT_ADMIN_USER_DISABLE,
        hint: this.translocoService.translate("Retirer l'utilisateur du groupe"),
        icon: '@tui.user-minus',
        appearance: 'outline-destructive',
        execute: (user: AdministrationUser): void => this.disableUser(user),
      },
    ],
    [UserAdminState.INVITED]: [this.deleteUserAction],
    [UserAdminState.DISABLED]: [
      {
        id: MenuActionId.DEPARTMENT_ADMIN_USER_ENABLE,
        hint: this.translocoService.translate("Ajouter l'utilisateur au groupe"),
        icon: '@tui.user-plus',
        appearance: 'outline-destructive',
        condition: (user: AdministrationUser): boolean =>
          !!user.licenseState && writeAccessLicenseStates.includes(user.licenseState),
        execute: (user: AdministrationUser): void => this.enableUser(user),
      },
    ],
  };

  readonly tableHeaderActions: MenuAction[] = [
    {
      id: MenuActionId.CLIENT_ADMIN_USER_INVITE,
      label: this.translocoService.translate('Inviter'),
      icon: '@tui.user-plus',
      appearance: 'outline',
      execute: (): void => this.inviteUsers(),
    },
  ];

  readonly tableHeaderActionButtons: ActionButton[] = [
    {
      appearance: 'primary',
      title: this.translocoService.translate('Inviter'),
      actionIds: [MenuActionId.CLIENT_ADMIN_USER_INVITE],
    },
  ];

  ngOnInit(): void {
    this.filterBarService.init();
    this.departmentUsersAdminService.init();
  }

  protected matcher(item: AdministrationUser, search: string): boolean {
    return match(search, [item.firstName, item.lastName, item.mailAddress]);
  }

  enableUser(user: AdministrationUser): void {
    this.departmentUsersAdminService.enableUser(user);
  }

  disableUser(user: AdministrationUser): void {
    this.departmentUsersAdminService.disableUser(user);
  }

  deleteInvitedUser(user: AdministrationUser): void {
    this.departmentUsersAdminService.deleteInvitedUser(user);
  }

  adminChange(user: AdministrationUser): void {
    this.departmentUsersAdminService.adminChange(user);
  }

  inviteUsers(): void {
    this.departmentUsersAdminService.addUsers();
  }
}
